/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, Fragment, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Flex, Button } from "@duxton-web/ui"
import { replace } from "lodash"
import { ItemAvailabilityModal } from "components/modals/ItemAvailabilityModal"
import { BottomDrawer } from "components/common"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { MarkStatusesText, availableStatusesValue } from "utils/item_helper"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory, EventEntryPoint } from "constants/tracking"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import useInventoryContext from "hooks/useInventoryContext"
import {
  resolveV3SelectionForAPI,
  computeV3SelectionSummary,
} from "utils/bulk_selection_helpers"
import {
  flattenInfiniteHierarchies,
  useMenuV3HierarchiesInfinite,
  partialMenuApi,
} from "queries"
import { mapHierarchyEntitiesToDepartments } from "utils/menuV3Mapper"

export interface MultiItemSelectionDrawerV3Props {
  visible: boolean
  onClose(e: any): void
}

export const MultiItemSelectionDrawerV3: FC<MultiItemSelectionDrawerV3Props> = ({
  visible,
  onClose,
}: MultiItemSelectionDrawerV3Props) => {
  const [showStatusModal, toggleStatusModal] = useState(false)
  const dispatch = useDispatch()
  const { isStatusChangeAllowed } = useMenuEditorStatus()
  const { disableAvailability } = useInventoryContext()

  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state.menu)

  const bulkSelectionsV3 = useSelector(
    (state: AppState) => state.bulkSelectionsV3,
  )

  // Read departments from RTK Query cache — no new network request, data is already loaded
  const { currentData: deptData } = useMenuV3HierarchiesInfinite(
    { merchantID, merchantGroupID },
    { skip: !merchantID },
  )
  const departments = useMemo(
    () =>
      mapHierarchyEntitiesToDepartments(
        flattenInfiniteHierarchies(deptData) ?? [],
      ),
    [deptData],
  )

  const { itemCount } = computeV3SelectionSummary(bulkSelectionsV3, departments)
  const isEmpty = itemCount === 0

  const markStatusesTextPlural = MarkStatusesText(true)
  const markStatusesTextSingular = MarkStatusesText(false)

  const textMap = useTranslationsMap({
    changeAvailability: "menu.buttons.changeAvailability",
    cancelButton: "menu.buttons.cancel",
  })

  const selectionLabel = useTranslation(
    itemCount === 1
      ? "menu.overview.itemSelected"
      : "menu.overview.itemsSelected",
    { "<x>": `${itemCount}` },
  )

  const handleChangeAvailability = async (availableStatus: number) => {
    const {
      departmentIDs,
      subDepartmentIDs,
      itemIDs,
      excludedSubDepartmentIDs,
      excludedItemIDs,
    } = resolveV3SelectionForAPI(bulkSelectionsV3)

    const totalSelected =
      departmentIDs.length + subDepartmentIDs.length + itemIDs.length
    const successMessage =
      totalSelected === 1
        ? markStatusesTextSingular[availableStatus]
        : replace(
            markStatusesTextPlural[availableStatus],
            "<x>",
            `${totalSelected}`,
          )

    const resp: any = await dispatch(
      actionCreator.menu.updateItemsAvailableStatus({
        merchantID,
        departmentIDs,
        subDepartmentIDs,
        itemIDs,
        excludedSubDepartmentIDs,
        excludedItemIDs,
        availableStatus,
        successMessage,
      }),
    )

    if (resp?.type.endsWith("_SUCCESS")) {
      dispatch(
        partialMenuApi.util.invalidateTags([
          "menuV3Items",
          "menuV3Hierarchies",
        ]),
      )
    }

    const labels = {
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
      ENTRY_POINT: EventEntryPoint.menuOverviewBottomNav,
      DEPARTMENT_ID: departmentIDs,
      SUB_DEPARTMENT_ID: subDepartmentIDs,
      ITEM_ID: itemIDs,
      VALUE: availableStatusesValue[availableStatus],
      STATUS: resp?.type.endsWith("_FAIL") ? "FAILURE" : "SUCCESS",
      ...(resp?.type.endsWith("_FAIL") && getResponseErrorEventLabels(resp)),
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.submitItemAvailability,
      labels,
    })
  }

  const handleClickChangeAvailability = () => {
    const {
      departmentIDs,
      subDepartmentIDs,
      itemIDs,
    } = resolveV3SelectionForAPI(bulkSelectionsV3)

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickChangeAvailability,
      labels: {
        DEPARTMENT_ID: departmentIDs,
        SUB_DEPARTMENT_ID: subDepartmentIDs,
        ITEM_ID: itemIDs,
        PAGELAYOUT: "BOTTOMNAV",
      },
    })
    toggleStatusModal(true)
  }

  return (
    <Fragment>
      <BottomDrawer
        data-testid="bottom-drawer-v3"
        visible={visible}
        onClose={e => {
          const {
            departmentIDs,
            subDepartmentIDs,
            itemIDs,
          } = resolveV3SelectionForAPI(bulkSelectionsV3)
          TroyWebTracker.sendEvent({
            category: EventCategory.menuOverviewTab,
            action: EventAction.clickExitItemAction,
            labels: {
              DEPARTMENT_ID: departmentIDs,
              SUB_DEPARTMENT_ID: subDepartmentIDs,
              ITEM_ID: itemIDs,
              PAGELAYOUT: "BOTTOMNAV",
            },
          })
          onClose(e)
        }}
      >
        <Flex align="center" gap={16}>
          <div style={{ fontWeight: 500, width: 200 }}>{selectionLabel}</div>

          {!disableAvailability && (
            <Button
              data-testid="change-availability-button-v3"
              size="small"
              onClick={handleClickChangeAvailability}
              disabled={!isStatusChangeAllowed || isEmpty}
            >
              {textMap.changeAvailability}
            </Button>
          )}
        </Flex>
      </BottomDrawer>

      <ItemAvailabilityModal
        data-testid="item-availability-modal-v3"
        visible={showStatusModal}
        hasRecurring={false}
        hasNonRecurring
        itemCount={itemCount}
        onClose={() => toggleStatusModal(false)}
        onConfirm={handleChangeAvailability}
      />
    </Fragment>
  )
}
